/**
 * Shared live observer for reactive entity types (view, external).
 *
 * When invalidation events fire, checks if any Socket.IO clients are subscribed
 * to the entity's room and pushes an entity.updated event. Debounced at 500ms.
 */

import type { ModuleContext } from '@gzl10/nexus-sdk'
import { entityRoom } from '@gzl10/nexus-sdk'

interface LiveObserverOptions {
  ctx: ModuleContext
  events: string[]
  moduleName: string
  entityKey: string
  label: unknown
}

/**
 * Register a debounced observer that pushes entity.updated when source events fire.
 *
 * LIMITATION: lazy import from core/ — runtime should not depend on core,
 * but live push requires socket room check. Acceptable until event bridge provides this.
 */
export function registerLiveObserver(options: LiveObserverOptions): void {
  const { ctx, events, moduleName, entityKey, label } = options

  import('@kernel/events/socket.js').then(({ getRoomSize, isSocketIOInitialized }) => {
    const handler = async () => {
      if (!isSocketIOInitialized()) return

      const room = entityRoom(ctx.tenantId, moduleName, entityKey)
      const roomSize = await getRoomSize(room)
      if (roomSize === 0) return

      ctx.core.logger.debug({ entity: label, room, roomSize }, 'Live push triggered')

      ctx.core.events.emit('entity.updated', {
        tenantId: ctx.tenantId,
        module: moduleName,
        entity: entityKey,
        action: 'updated' as const,
        id: '*',
        data: undefined,
        timestamp: new Date().toISOString()
      })
    }

    for (const event of events) {
      let timer: ReturnType<typeof setTimeout> | null = null
      ;(ctx.core.events as unknown as { on: (e: string, fn: () => void) => void }).on(event, () => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(handler, 500)
      })
    }
  })
}
